import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useUser } from "@/features/auth/store";
import { isSuperAdmin } from "@/lib/auth-utils";
import { actorMayManageUser } from "@/lib/role-management-policy";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { EditUserDialog } from "./EditUserDialog";
import { DeactivateUserDialog } from "./DeactivateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { UserActivitySheet } from "./UserActivitySheet";
import type { User } from "@/types/user";

interface UserRowActionsProps {
  user: User;
}

export function UserRowActions({ user }: UserRowActionsProps) {
  const sessionUser = useUser();
  const {
    canEdit,
    canDeactivate,
    canViewActivity,
    canManagePermissions,
    canManageEditorPermissions,
  } = useUserPermissions();
  const manageable = actorMayManageUser(sessionUser, user);

  return (
    <div className="flex gap-2 items-center justify-end">
      {canViewActivity && <UserActivitySheet user={user} />}
      {(canManagePermissions || canManageEditorPermissions) && manageable && (
        <Link
          to={`${appPath(Pages.USERS, "permissions")}?userId=${user.id}&tab=user`}
          className="inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-primary"
          aria-label="إدارة صلاحيات المستخدم"
          title="الصلاحيات"
        >
          <ShieldCheck className="h-4 w-4" />
        </Link>
      )}
      {canEdit && manageable && <EditUserDialog user={user} />}
      {canDeactivate && manageable && (
        <DeactivateUserDialog userId={user.id} isActive={user.isActive} />
      )}
      {isSuperAdmin(sessionUser) && sessionUser?.id !== user.id && (
        <DeleteUserDialog userId={user.id} />
      )}
    </div>
  );
}
